<template>
  <tab-item-template>
    <el-table border :data="eventProfileView">
      <el-table-column
          label="名称"
          prop="eventProfileableName"
          sortable>
        <template #header>
          名称
          <el-tooltip content="カレンダの名称、またはイベントの名称" placement="top">
            <v-icon small color="blue">mdi-information</v-icon>
          </el-tooltip>
        </template>
        <template #default="{row}">
          <template v-if="row.eventProfileableType === 'EventImplementation'">
            <table-inline>
              <text-field-like
                  :value="row.eventProfileableName"
                  :value-local-id="row.eventProfileableLocalId"
                  placeholder="名称未設定">
                <template #prepend>
                  <el-tooltip :content="eventIconTooltip" :open-delay="500" placement="left">
                    <v-icon>mdi-lightning-bolt</v-icon>
                  </el-tooltip>
                </template>
              </text-field-like>
            </table-inline>
          </template>
          <template v-else-if="row.eventProfileableType === 'CalendarImplementation'">
            <table-inline>
              <text-field-like
                  :value="row.eventProfileableName"
                  placeholder="-">
                <template #prepend>
                  <el-tooltip content="カレンダ" :open-delay="500" placement="left">
                    <v-icon>mdi-calendar</v-icon>
                  </el-tooltip>
                </template>
              </text-field-like>
            </table-inline>
          </template>
        </template>
      </el-table-column>
      <el-table-column
          label="説明"
          prop="eventProfileableDescription">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.eventProfileableDescription"
                placeholder="未設定"
                multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          label="プロセス"
          prop="processComponentModel.name"
          sortable
      :sort-method="(a, b) => sortProcessName(a, b)">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.processComponentModel && row.processComponentModel.name"
                :placeholder="'\u2E3B'">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          label="記録"
          prop="eventImplementation.isMonitored"
          width="80"
          align="center"
          sortable
          :resizable="false">
        <template #default="{row}">
          <table-inline>
            <readonly-checkbox :value="row.eventImplementation && row.eventImplementation.isMonitored"></readonly-checkbox>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          label="記録内容"
          prop="eventImplementation.monitoringContent">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.eventImplementation && (row.eventImplementation.isMonitored ? true : null) && row.eventImplementation.monitoringContent"
                :placeholder="(row.eventProfileableType === 'EventImplementation' && row.eventImplementation && row.eventImplementation.isMonitored) ? '未設定' : '\u2E3B'"
                multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
    </el-table>
  </tab-item-template>
</template>

<script>
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import {mapActions, mapGetters, mapState} from "vuex";
import gql from "graphql-tag";
import uniq from "lodash/uniq";
import TableInline from "@/components/atoms/TableInline.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import natsort from "natsort";

/** ソート関数 */
const sortFn = natsort();

export default {
  name: "EventProfileTab",
  components: {ReadonlyCheckbox, TextFieldLike, TableInline, TabItemTemplate},
  props: {
    serviceProfile: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      eventProfileView: [],
    };
  },
  computed: {
    ...mapGetters({
      hdsEntities: 'hds/entities',
    }),
    serviceProfileId() {
      return this.serviceProfile.id;
    },
    eventIconTooltip() {
      switch(this.serviceProfile.serviceType) {
        case 'produce': return 'トリガとなるイベント';
        case 'consume': return 'イベント';
        default: return 'イベント';
      }
    },
  },
  methods: {
    ...mapActions({
      loadProcessImplementations: 'hcs/processImplementation/findList',
      loadEventProfiles: 'hcs/eventProfile/findList',
      loadEventImplementations: 'hcs/eventImplementation/findList',
      gqlQuery: 'hds/gqlQuery',
    }),
    sortProcessName(a, b) {
      const ap = a.processComponentModel?.name ?? '';
      const bp = b.processComponentModel?.name ?? '';
      return sortFn(ap, bp);
    }
  },
  watch: {
    serviceProfileId: {
      immediate: true,
      async handler() {
        const processImplementations = await this.loadProcessImplementations();
        const eventImplementations = await this.loadEventImplementations();
        const eventProfiles = (await this.loadEventProfiles({
          'q[service_profile_id_eq]': this.serviceProfileId
        }))
            .filter(ep => ep.serviceProfileId === this.serviceProfileId)
            .map(ep => {
              if(ep.eventProfileableType === 'EventImplementation') {
                const eventImplementation = eventImplementations.find(ei => ei.id === ep.eventProfileableId);
                const processImplementation = processImplementations.find(pi => pi.id === eventImplementation?.processImplementationId);
                return {
                  ...ep,
                  eventImplementation,
                  processImplementation
                };
              } else {
                return ep;
              }
            });

        const {processComponentModels} = await this.gqlQuery({
          query: gql`
            query FindProcessComponentModel(
                $processComponentModelIds: [ID!]!,
            ) {
                processComponentModels: processComponentModelInBulk(processComponentModelIds: $processComponentModelIds) {
                    id
                    name
                    description
                }
            }`,
          variables: {
            processComponentModelIds: uniq(eventProfiles
                .filter(ep => ep.processImplementation)
                .map(ep => ep.processImplementation.processComponentModelId)
            )
          }
        });

        this.eventProfileView = eventProfiles.map(ep => {
          const pi = ep.processImplementation;
          const processComponentModel = pi ? processComponentModels.find(pcm => pcm.id === pi.processComponentModelId) : undefined;
          return {
            ...ep,
            processComponentModel
          };
        });
      }
    }
  }
}
</script>

<style scoped>

</style>
